import React from 'react'
import { CheckIcon, TimesIcon } from 'src/components/icons'
import useIsMounted from './useIsMounted'
import useHandler from './useHandler'

const PENDING = Symbol('pending')
const FULFILLED = Symbol('fulfilled')
const REJECTED = Symbol('rejected')

const states = {
  [PENDING]: {
    loading: true,
    disabled: true,
  },
  [FULFILLED]: {
    icon: CheckIcon,
    color: 'success',
    disabled: true,
  },
  [REJECTED]: {
    icon: TimesIcon,
    color: 'danger',
    disabled: true,
  },
}

/**
 * Enhance a onClick handler in order to provide props related to the current promise state it returns
 * @param {Object} props - props of the calling component containing the onClick handler
 * @return {Object} - object containing the enhanced handler along with props related to its current promise state
 */
export default function useOnClickPromiseStates({ onClick }) {
  // create a state for the current promise state
  const [state, setState] = React.useState()

  // get isMounted checker
  const isMounted = useIsMounted()

  // enhance onClick
  const _onClick = useHandler((...args) => {
    // set loading state on click
    setState(PENDING)

    // return promise that resolves original handler
    return (
      Promise.resolve(onClick(...args))

        // set fulfilled state and return response
        .then(response => {
          isMounted() && setState(FULFILLED)
          return response
        })

        // set error state and rethrow error
        .catch(() => isMounted() && setState(REJECTED))

        // finally reset state after 3 seconds
        .finally(() => setTimeout(() => isMounted() && setState(), 3000))
    )
  })

  // return enhanced onClick along with props related to current promise state
  return React.useMemo(
    () => ({
      ...states[state],
      onClick: _onClick,
    }),
    [state, _onClick]
  )
}
